import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Users } from "lucide-react";
import {
  createUser,
  listAllStrategies,
  listUsers,
  type AdminUserCreate,
  type BrokerPolicyIn,
} from "../../api/admin";
import type { BrokerKind } from "../../api/brokers";
import { apiErrorMessage } from "../../lib/api";
import Button from "../../components/Button";
import Card from "../../components/Card";
import Chip from "../../components/Chip";
import EmptyState from "../../components/EmptyState";

const BROKERS: BrokerKind[] = ["zerodha", "dhan", "delta", "upstox"];

/** Sensible starting point: trading-capable on one Zerodha account, able to
 *  backtest, but NOT able to backfill — backfills write to the shared bar store
 *  and burn the shared broker rate budget. */
const DEFAULT_POLICIES: BrokerPolicyIn[] = BROKERS.map((b) => ({
  broker: b,
  enabled: b === "zerodha",
  max_accounts: b === "zerodha" ? 1 : 0,
}));

export default function AdminUsersPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);

  const users = useQuery({ queryKey: ["admin", "users"], queryFn: listUsers });
  const strategies = useQuery({
    queryKey: ["admin", "strategies"],
    queryFn: listAllStrategies,
  });

  const create = useMutation({
    mutationFn: (payload: AdminUserCreate) => createUser(payload),
    onSuccess: (u) => {
      void qc.invalidateQueries({ queryKey: ["admin", "users"] });
      setShowCreate(false);
      navigate(`/admin/users/${u.id}`);
    },
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Users</h1>
          <p className="text-sm text-muted mt-0.5">
            Create users and control which brokers, strategies and features each
            one can use.
          </p>
        </div>
        <Button iconLeft={<UserPlus size={15} />} onClick={() => setShowCreate(true)}>
          New user
        </Button>
      </div>

      {showCreate && (
        <CreateUserForm
          strategyKeys={(strategies.data ?? []).map((s) => s.key)}
          submitting={create.isPending}
          error={create.error ? apiErrorMessage(create.error) : null}
          onCancel={() => setShowCreate(false)}
          onSubmit={(p) => create.mutate(p)}
        />
      )}

      <Card>
        {users.isLoading ? (
          <div className="text-sm text-muted p-4">Loading…</div>
        ) : (users.data ?? []).length === 0 ? (
          <EmptyState icon={<Users size={28} />} title="No users yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted">
                <tr className="border-b border-border">
                  <th className="px-4 py-2 font-medium">Email</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Role</th>
                  <th className="px-4 py-2 font-medium">Features</th>
                  <th className="px-4 py-2 font-medium">Last login</th>
                </tr>
              </thead>
              <tbody>
                {(users.data ?? []).map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => navigate(`/admin/users/${u.id}`)}
                    className="border-b border-border/60 last:border-0 hover:bg-surface-2 cursor-pointer"
                  >
                    <td className="px-4 py-2.5">{u.email}</td>
                    <td className="px-4 py-2.5 text-muted">
                      {u.display_name || "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-1.5">
                        {u.is_admin && <Chip tone="brand">admin</Chip>}
                        {!u.is_active && <Chip tone="down">inactive</Chip>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-1.5">
                        {u.can_backtest && <Chip>backtest</Chip>}
                        {u.can_backfill && <Chip>backfill</Chip>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-muted">
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleString()
                        : "never"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function CreateUserForm({
  strategyKeys,
  submitting,
  error,
  onCancel,
  onSubmit,
}: {
  strategyKeys: string[];
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (p: AdminUserCreate) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [canBacktest, setCanBacktest] = useState(true);
  const [canBackfill, setCanBackfill] = useState(false);
  const [grantAll, setGrantAll] = useState(true);
  const [policies, setPolicies] = useState<BrokerPolicyIn[]>(DEFAULT_POLICIES);

  return (
    <Card title="New user" subtitle="Access is deny-by-default — grant explicitly.">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            email,
            password,
            display_name: displayName || null,
            is_admin: isAdmin,
            can_backtest: canBacktest,
            can_backfill: canBackfill,
            grant_all_strategies: grantAll,
            strategy_keys: grantAll ? [] : [],
            broker_policies: policies,
          });
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Email">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Password" hint="Minimum 8 characters.">
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Display name">
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>

        <div className="space-y-2">
          <Toggle checked={isAdmin} onChange={setIsAdmin} label="Administrator">
            Can manage users and their limits. Cannot see other users' trading data.
          </Toggle>
          <Toggle checked={canBacktest} onChange={setCanBacktest} label="Backtesting" />
          <Toggle checked={canBackfill} onChange={setCanBackfill} label="Data backfill">
            Shows the Data page for bulk backfills into the shared market-data
            store. Not needed to download a symbol from the Chart — every user
            can do that through their own broker connection.
          </Toggle>
          <Toggle
            checked={grantAll}
            onChange={setGrantAll}
            label={`Grant all ${strategyKeys.length} strategies available today`}
          >
            Strategies added later are not granted automatically — that's the
            point of per-user grants. Fine-tune on the user's page afterwards.
          </Toggle>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wide text-muted mb-2">
            Broker access
          </div>
          <div className="space-y-1.5">
            {policies.map((p, i) => (
              <div key={p.broker} className="flex items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={p.enabled}
                  onChange={(e) => {
                    const next = [...policies];
                    next[i] = {
                      ...p,
                      enabled: e.target.checked,
                      max_accounts: e.target.checked
                        ? Math.max(1, p.max_accounts)
                        : 0,
                    };
                    setPolicies(next);
                  }}
                />
                <span className="w-24 capitalize">{p.broker}</span>
                <label className="text-muted text-xs">max accounts</label>
                <input
                  type="number"
                  min={0}
                  max={50}
                  disabled={!p.enabled}
                  value={p.max_accounts}
                  onChange={(e) => {
                    const next = [...policies];
                    next[i] = { ...p, max_accounts: Number(e.target.value) };
                    setPolicies(next);
                  }}
                  className="w-20 rounded border border-border bg-surface px-2 py-1 text-sm disabled:opacity-40"
                />
              </div>
            ))}
          </div>
        </div>

        {error && <div className="text-sm text-down-600 dark:text-down-400">{error}</div>}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creating…" : "Create user"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

const inputCls =
  "w-full rounded border border-border bg-surface px-2.5 py-1.5 text-sm";

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="text-xs text-muted mb-1">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-muted mt-1">{hint}</div>}
    </label>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  children,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  children?: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-start gap-2.5 text-sm">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5"
      />
      <span>
        <span className={disabled ? "text-muted" : ""}>{label}</span>
        {children && (
          <span className="block text-[11px] text-muted mt-0.5">{children}</span>
        )}
      </span>
    </label>
  );
}
